import { Button, FormGroup, H4, Switch, Tab, Tabs } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classnames from "classnames";
import React from "react";

import ArrayItemToolbar from "common/form/common/ArrayItemToolbar";
import DropdownSelect from "common/form/widgets/common/DropdownSelect";
import CssUnitWidget from "common/form/widgets/CssUnitWidget";
import { FlexAlignItems, FlexDirectionWidget, FlexJustifyWidget, FlexWrapWidget } from "common/form/widgets/FlexWidgets";
import NumberWidget from "common/form/widgets/NumberWidget";
import VerticalCenter from "common/fragments/VerticalCenter";
import { IFieldProps } from "decorator-form";
import { ILayoutType, LayoutTypeRegistry } from "../ILayoutType";
import styled, { css } from "styled-components";

interface IFlexRegion {
	flexGrow: number;
	flexShrink: number;
	minWidth: string;
	maxWidth: string;
	regionUuid: string;
}

interface IFlexLayout {
	flexDirection?: "row" | "row-reverse" | "column" | "column-reverse";
	flexWrap?: "wrap" | "nowrap" | "wrap-reverse";
	justifyContent?: "flex-start" | "flex-end" | "center" | "space-between" | "space-around" | "space-evenly";
	alignItems?: "stretch" | "flex-start" | "flex-end" | "center" | "baseline";
	alignContent?: "flex-start" | "flex-end" | "center" | "space-between" | "space-around" | "stretch";
	regions: IFlexRegion[];
	minHeight: string;
	autoHeight: boolean;
	gap: string;
}

const RegionWrapper = styled.div<{layout: IFlexLayout}>(({layout}) => css`

	${layout.gap && (layout.flexDirection === "column" || layout.flexDirection === "column-reverse") && css`
		margin-bottom: ${layout.gap};

		&:last-child {
			margin-bottom: 0;
		}
	`}

	${layout.gap && (!layout.flexDirection || layout.flexDirection === "row" || layout.flexDirection === "row-reverse") && css`
		margin-right: ${layout.gap};

		&:last-child {
			margin-right: 0;
		}
	`}
`);

const styles = {
	containerPreview: {
		backgroundColor: "rgba(0,0,0,0.2)",
		border: "1px solid rgba(0,0,0,0.3)",
		minHeight: 200
	},
	itemPreview: {
		backgroundColor: "rgba(19,124,189,0.2)",
		border: "1px solid rgba(19,124,189,0.3)",
		borderRadius: "4px",
		color: "rgb(19,124,189)",
		minHeight: "30px",
		maxWidth: '100%',
		maxHeight: '100%'
	}
};

interface IFlexLayoutFieldProps extends IFieldProps<IFlexLayout> {
	regions: { [uuid: string]: { __editName: string; } };
}

class FlexLayoutField extends React.Component<IFlexLayoutFieldProps> {

	public render() {
		const { formData = {} as IFlexLayout, regions } = this.props;

		return (
			<Tabs id="layout:flex">
				<Tab id="container" title="Container" panel={
					<FormGroup>
						<FormGroup label="Gap / Spacing">
							<CssUnitWidget onChange={(gap) => this.partialChange({gap})} value={formData.gap} />
						</FormGroup>
						<FormGroup label="Direction">
							<FlexDirectionWidget
								onChange={this.onChangeField('flexDirection')}
								value={formData.flexDirection}
							/>
						</FormGroup>
						<FormGroup label="Wrap">
							<FlexWrapWidget
								onChange={this.onChangeField('flexWrap')}
								value={formData.flexWrap}
							/>
						</FormGroup>
						<FormGroup label="Justify">
							<FlexJustifyWidget
								onChange={this.onChangeField('justifyContent')}
								value={formData.justifyContent}
							/>
						</FormGroup>
						<FormGroup label="Align Items">
							<FlexAlignItems
								onChange={this.onChangeField('alignItems')}
								value={formData.alignItems}
							/>
						</FormGroup>
						<FormGroup label="Align Content">
							<FlexAlignItems
								onChange={this.onChangeField('alignContent')}
								value={formData.alignContent}
							/>
						</FormGroup>
						<FormGroup label="Minimum height">
							<CssUnitWidget
								onChange={this.onChangeField('minHeight')}
								value={formData.minHeight}
							/>
						</FormGroup>
						<FormGroup label="Auto-height resize">
							<Switch checked={formData.autoHeight} onChange={this.onChangeField('autoHeight')} />
						</FormGroup>
					</FormGroup>
				} />
				<Tab id="regions" title="Items" panel={
					<FormGroup>
						{
							formData.regions && formData.regions.map((region, ri) => (
								<FormGroup key={ri}>
									<div style={{display: 'flex', justifyContent: 'space-between'}}>
										<H4>{`Region ${ri + 1}`}</H4>
										<ArrayItemToolbar itemIndex={ri} arr={formData.regions} onChange={this.onChangeRegions} />
									</div>
									<FormGroup label="Region">
										<DropdownSelect
											value={region.regionUuid}
											onChange={this.onChangeRegionField(ri, 'regionUuid')}
											options={Object.entries(regions || {}).map(([uuid, reg]) => ({
												value: uuid,
												label: reg.__editName || "Untitled Region"
											}))}
										/>
									</FormGroup>
									<FormGroup label="Grow" helperText="(positive numbers only)">
										<NumberWidget.Positive
											onChange={this.onChangeRegionField(ri, 'flexGrow')}
											value={region.flexGrow}
										/>
									</FormGroup>
									<FormGroup label="Shrink" helperText="(positive numbers only)">
										<NumberWidget.Positive
											onChange={this.onChangeRegionField(ri, 'flexShrink')}
											value={region.flexShrink}
										/>
									</FormGroup>
									<FormGroup label="Minimum Width">
										<CssUnitWidget
											onChange={this.onChangeRegionField(ri, 'minWidth')}
											value={region.minWidth}
										/>
									</FormGroup>
									<FormGroup label="Maximum Width">
										<CssUnitWidget
											onChange={this.onChangeRegionField(ri, 'maxWidth')}
											value={region.maxWidth}
										/>
									</FormGroup>
								</FormGroup>
							))
						}
						<FormGroup>
							<Button small={true} text="Add Region" onClick={this.addRegion} />
						</FormGroup>
					</FormGroup>
				} />
			</Tabs>
		);
	}

	private onChangeField = (field: keyof IFlexLayout) => (value: any) => {
		const { formData = {} as IFlexLayout, onChange } = this.props;
		onChange({
			...formData,
			[field]: value
		});
	}

	private onChangeRegionField = (index: number, field: keyof IFlexRegion) => (value: any) => {
		const { formData = {} as IFlexLayout, onChange } = this.props;
		onChange({
			...formData,
			regions: formData.regions.map((r, ri) => ri === index ? { ...r, [field]: value } : r)
		});
	}

	private onChangeRegions = (regions: IFlexRegion[]) => {
		const { formData = {} as IFlexLayout, onChange } = this.props;
		onChange({...formData, regions});
	}

	private addRegion = () => {
		const { formData = {} as IFlexLayout, onChange } = this.props;
		onChange({
			...formData,
			regions: [...formData.regions || [], {} as IFlexRegion]
		});
	}

	private partialChange = (value: Partial<IFlexLayout>) => {
		const { onChange, formData = {} as IFlexLayout } = this.props;
		onChange({
			...formData,
			...value
		});
	}
}

const FlexLayout: ILayoutType<IFlexLayout> = {
	type: "flex",
	label: "Flex",
	icon: IconNames.ALIGNMENT_HORIZONTAL_CENTER,
	renderPreview: (data = {} as IFlexLayout, rd) => {
		const { regions, ...restData } = data;

		return (
			<div style={{display: 'flex', ...restData, ...styles.containerPreview}}>
				{regions && regions.map((region, ri) => {
					const { regionUuid, ...restRegion } = region;

					return (
						<RegionWrapper layout={data} style={{...restRegion, ...styles.itemPreview}}>
							<VerticalCenter style={{height: '100%'}}>
								{
									region.regionUuid ? (
										rd[region.regionUuid] && rd[region.regionUuid].__editName || "Invalid Region!"
									) : "(Select a Region)"
								}
							</VerticalCenter>
						</RegionWrapper>
					);
				})}
			</div>
		);
	},
	renderInput: (data, onChange, regions) => {

		return (
			<FlexLayoutField formData={data || {} as IFlexLayout} onChange={onChange} uiSchema={undefined} idSchema={undefined} regions={regions} />
		);
	},
	renderLayout: ({regions, ...restData}, renderRegion, layoutUuid) => (
		<div
			style={{display: 'flex', ...restData}}
			className={classnames({"auto-height": restData.autoHeight})}
			data-uuid={layoutUuid}>
			{regions && regions.map(({regionUuid, ...restRegion}, ri) => {
				
				return (
					<RegionWrapper key={ri} layout={{regions, ...restData}} style={restRegion}>
						{renderRegion(regionUuid, ri)}
					</RegionWrapper>
				)
			})}
		</div>
	)
};

export default LayoutTypeRegistry.registerType(FlexLayout);



// WEBPACK FOOTER //
// ./src/common/form/fields/layout/layoutTypes/FlexLayout.tsx